import React, { useState } from "react";

interface Template {
	id: number;
	title: string;
	content: string;
}

interface QuickResponseTemplatesProps {
	onTemplateSelect: (template: Template) => void;
	onClose: () => void;
}

const QuickResponseTemplates: React.FC<QuickResponseTemplatesProps> = ({
	onTemplateSelect,
	onClose,
}) => {
	const [selectedCategory, setSelectedCategory] = useState<
		"general" | "viewing" | "pricing" | "followup"
	>("general");

	const templates: Record<
		"general" | "viewing" | "pricing" | "followup",
		Template[]
	> = {
		general: [
			{
				id: 1,
				title: "Welcome Message",
				content:
					"Hello! Thank you for your interest in our properties. How can I assist you today?",
			},
			{
				id: 2,
				title: "Property Inquiry Response",
				content:
					"Thank you for your inquiry about this property. I'd be happy to provide you with more information and schedule a viewing at your convenience.",
			},
			{
				id: 3,
				title: "Availability Confirmation",
				content:
					"This property is currently available for viewing. Would you like to schedule an appointment this week?",
			},
		],
		viewing: [
			{
				id: 4,
				title: "Schedule Viewing",
				content:
					"I'd be happy to schedule a property viewing for you. What days and times work best for your schedule?",
			},
			{
				id: 5,
				title: "Viewing Confirmation",
				content:
					"Your viewing has been confirmed. I'll send you the exact address and contact details shortly. Please let me know if you need to reschedule.",
			},
			{
				id: 6,
				title: "Viewing Reminder",
				content:
					"This is a friendly reminder about your property viewing scheduled for tomorrow. Looking forward to showing you the property!",
			},
		],
		pricing: [
			{
				id: 7,
				title: "Price Information",
				content:
					"The asking price for this property is competitive with the current market. I can provide you with a detailed breakdown and recent comparable sales.",
			},
			{
				id: 8,
				title: "Negotiation Discussion",
				content:
					"I understand you're interested in discussing the price. Let me review your offer with the seller and get back to you as soon as possible.",
			},
			{
				id: 9,
				title: "Financing Assistance",
				content:
					"I can connect you with trusted mortgage advisors who can help you with financing options for this property.",
			},
		],
		followup: [
			{
				id: 10,
				title: "Post-Viewing Follow-up",
				content:
					"Thank you for viewing the property today. What were your thoughts? Do you have any questions or would you like to schedule a second viewing?",
			},
			{
				id: 11,
				title: "Decision Timeline",
				content:
					"I wanted to follow up on your property viewing. Do you have a timeline for making a decision? I'm here to help with any additional information you need.",
			},
			{
				id: 12,
				title: "Alternative Suggestions",
				content:
					"Based on your preferences, I have a few other properties that might interest you. Would you like me to send you the details?",
			},
		],
	};

	const categories = [
		{ id: "general", label: "General", icon: "message-circle" },
		{ id: "viewing", label: "Viewings", icon: "calendar" },
		{ id: "pricing", label: "Pricing", icon: "dollar-sign" },
		{ id: "followup", label: "Follow-up", icon: "clock" },
	];

	return (
		<div className="border-b border-border bg-card">
			<div className="p-4">
				<div className="flex items-center justify-between mb-4">
					<h3 className="font-medium text-foreground">
						Quick Response Templates
					</h3>
					<button
						onClick={onClose}
						className="p-1 hover:bg-muted rounded-full transition-colors"
					>
						<svg
							className="w-4 h-4 text-muted-foreground"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				{/* Category Tabs */}
				<div className="flex space-x-1 mb-4 bg-muted rounded-lg p-1">
					{categories?.map((category) => (
						<button
							key={category?.id}
							onClick={() =>
								setSelectedCategory(
									category?.id as
										| "general"
										| "viewing"
										| "pricing"
										| "followup",
								)
							}
							className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
								selectedCategory === category?.id
									? "bg-background text-foreground shadow-sm"
									: "text-muted-foreground hover:text-foreground"
							}`}
						>
							{category?.label}
						</button>
					))}
				</div>

				{/* Templates Grid */}
				<div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
					{templates?.[selectedCategory]?.map((template: Template) => (
						<button
							key={template?.id}
							onClick={() => onTemplateSelect(template)}
							className="p-3 text-left border border-border rounded-lg hover:bg-muted/50 transition-colors group"
						>
							<div className="font-medium text-sm text-foreground mb-1 group-hover:text-primary">
								{template?.title}
							</div>
							<div className="text-xs text-muted-foreground line-clamp-2">
								{template?.content}
							</div>
						</button>
					))}
				</div>

				{/* Custom Template Input */}
				<div className="mt-4 pt-4 border-t border-border">
					<div className="text-xs text-muted-foreground mb-2">
						Create Custom Template
					</div>
					<div className="flex space-x-2">
						<input
							type="text"
							placeholder="Template title..."
							className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
						/>
						<button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm">
							Save
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default QuickResponseTemplates;
